/* eslint-disable @typescript-eslint/no-unused-vars */
import classNames from 'classnames';
import moment, { Moment } from 'moment';
import { DownloadOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  downloadInvoice,
  getInvoiceHistory,
} from '../../../../../api/billing-center/invoice';
import { AutoTable } from '../../../../../components/auto-table';
import { CommonButton } from '../../../../../components/button/main';
import { CommonCard } from '../../../../../components/card';
import { CommonRangePicker } from '../../../../../components/date-picker';
import { FilterGroup } from '../../../../../components/filter-group';
import { CommonSearchInput } from '../../../../../components/input';
import { getLocalTime } from '../../../../../lib';
import getFormatMessage from '../../../../../locales/get-format-message';
import {
  IAPIInvoiceHistoryItem,
  InvoiceStatus,
  InvoiceType,
} from '../../../../../model/api-modules';
import { IRootState } from '../../../../../store/reducers';
import { GetformatMoney } from '../../billing-overview/component/fomat-money';
import styles from './index.module.scss';
import {
  getFilterList,
  INVOICE_STATUS_OPTIONS,
  INVOICE_TYPE_OPTIONS,
} from '../lib';
import { TableFilterMode } from '../../../../../components/table';

interface IFilterValues {
  invoice_no?: string;
  create_time?: [Moment, Moment];
  invoice_type?: InvoiceType;
  invoice_status?: InvoiceStatus;
}

export const ApplyInvoiceHistory = () => {
  const selectStudio = useSelector(
    (state: IRootState) => state.studio.selectStudio
  );

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [filterValues, setFilterValues] = useState<IFilterValues>({});
  const [list, setList] = useState<ReadonlyArray<IAPIInvoiceHistoryItem>>([]);
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);

  useEffect(() => {
    init();
  }, [selectStudio]);

  const init = async () => {
    if (!selectStudio) {
      return;
    }
    const res = await getInvoiceHistory(selectStudio.studioID);
    setList(res);
  };

  const download = async (records: IAPIInvoiceHistoryItem[]) => {
    const buffer = await downloadInvoice(records.map((item) => item.apply_id));
    if (!buffer) {
      return;
    }
    const blob = new Blob([buffer]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.target = '_blank';
    link.href = url;
    link.download = `invoice-${moment(new Date()).format(
      'YYYYMMDDHHmmss'
    )}.zip`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const totalMoney = list.reduce((acc, item) => acc + Number(item.amount), 0);

  const filterList = getFilterList([...list], filterValues);

  return (
    <CommonCard
      title={
        <>
          <span className={styles.moneyLabel}>
            {getFormatMessage('apply_total_invoice_amount')}：
          </span>
          <GetformatMoney money={totalMoney} className={styles.moneyText} />
        </>
      }
      extra={
        <FilterGroup
          onFilterChange={(values: IFilterValues) => {
            setFilterValues({ ...filterValues, ...values });
          }}
        >
          <CommonRangePicker name="create_time" />
          <CommonSearchInput
            name="invoice_no"
            placeholder={getFormatMessage('输入发票号码查找')}
          />
          <CommonButton
            className={styles.downloadBtn}
            onClick={() => {
              download(filterList);
            }}
          >
            <DownloadOutlined />
            {getFormatMessage('download_all')}
          </CommonButton>
        </FilterGroup>
      }
    >
      <AutoTable
        list={filterList}
        tableExtension={{
          pagination: {
            current: page,
            total: filterList.length,
            pageSize,
            showTotal: () => {
              return `Total ${filterList.length} items`;
            },
          },
        }}
        configFields={[
          {
            key: 'create_time',
            name: getFormatMessage('apply_invoice_time'),
            tableExtension: {
              renderRow(key, value, record) {
                return getLocalTime(value, userInfo?.UserSettings?.Timezone);
              },
            },
          },
          {
            key: 'apply_type',
            name: getFormatMessage('apply_invoice_road'),
            tableExtension: {
              renderRow(key, value, record) {
                return getFormatMessage('manual_apply_invoice');
              },
            },
          },
          {
            key: 'apply_type',
            name: getFormatMessage('invoice_header_name'),
            tableExtension: {
              renderRow(key, value, record) {
                return getFormatMessage('enterprise');
              },
            },
          },
          {
            key: 'invoice_type',
            name: getFormatMessage('invoice_type'),
            tableExtension: {
              renderRow(key, value, record) {
                const type = value as InvoiceType;
                const text =
                  INVOICE_TYPE_OPTIONS.find((item) => item.value === type)
                    ?.label || '';
                return text;
              },
              filters: {
                kind: TableFilterMode.menu,
                options: [
                  {
                    label: getFormatMessage('view_all'),
                    value: '',
                  },
                  ...INVOICE_TYPE_OPTIONS,
                ],
                onFilter: (val: InvoiceType, record) => {
                  return record.invoice_type === val || !val;
                },
              },
            },
          },
          {
            key: 'invoice_type',
            name: getFormatMessage('invoice_medium'),
            tableExtension: {
              renderRow(key, value, record) {
                return getFormatMessage('apply_electronic_invoice');
              },
            },
          },
          {
            key: 'invoice_no',
            name: getFormatMessage('invoice_id'),
          },
          {
            key: 'amount',
            name: getFormatMessage('apply_amount'),
          },
          {
            key: 'invoice_status',
            name: getFormatMessage('invoice_status'),
            tableExtension: {
              renderRow(key, value, record) {
                const status = value as InvoiceStatus;
                const text =
                  INVOICE_STATUS_OPTIONS.find(
                    (option) => option.value === value
                  )?.label || '';
                return (
                  <div>
                    <span
                      className={classNames(
                        styles.dot,
                        styles[`dot-${status}`]
                      )}
                    ></span>
                    <span>{text}</span>
                  </div>
                );
              },
              filters: {
                kind: TableFilterMode.menu,
                options: [
                  {
                    label: getFormatMessage('view_all'),
                    value: '',
                  },
                  ...INVOICE_STATUS_OPTIONS,
                ],
                onFilter: (val: InvoiceStatus, record) => {
                  return record.invoice_status === val || !val;
                },
              },
            },
          },
          {
            key: 'operate',
            name: getFormatMessage('operate'),
            tableExtension: {
              renderRow(key, value, record) {
                return (
                  <>
                    {record.invoice_status === InvoiceStatus.Success && (
                      <CommonButton
                        type="link"
                        onClick={() =>
                          download([record] as IAPIInvoiceHistoryItem[])
                        }
                      >
                        {getFormatMessage('down_invoice')}
                      </CommonButton>
                    )}
                  </>
                );
              },
            },
          },
        ]}
      />
    </CommonCard>
  );
};
